import { Avatar } from "./Avatar";
import type { Point, Vector2 } from "@mythic/utils";

export class AvatarController {
    public userId: string;
    public username: string;

    private _targetLocation: Point | null = null;

    public walkSpeed: number = 40;
    public runSpeed: number = 100;

    public avatar: Avatar;

    private _sliding: boolean = false;
    private _onSlideComplete: () => void = () => {};
    private _onSlide: (v: Vector2) => void = () => {};

    private _disposed: boolean = false;
    get disposed(): boolean {
        return this._disposed;
    }

    constructor(userId: string, username: string, avatar: Avatar) {
        this.userId = userId;
        this.username = username;
        this.avatar = avatar;
    }

    dispose = () => {
        this._disposed = true;
    };

    die = () => {
        this.avatar.state = "die";
    };

    idle = () => {
        this.avatar.state = "idle";
    };

    jump = () => {
        if (!this.avatar) return;

        this.avatar.state = "jump";
        this.avatar.animator.onAnimationComplete = () => {
            this.idle();
        };
    };

    run = () => {
        this.avatar.state = "run";
    };

    walk = () => {
        this.avatar.state = "walk";
    };

    update = (delta: number) => {
        if (this._sliding) {
            this._updateSlide(delta);
        }
    };

    // Stop the avatar from sliding immediately
    halt = () => {
        // Stop sliding and clear the target location.
        this._sliding = false;
        this._targetLocation = null;
    };

    // x and y should be -1, 0, or 1
    // This function moves the avatar to the specified direction.
    // and updates the avatar's direction based on the movement.
    move = (velocity: Vector2): Vector2 => {
        if (!this.avatar) return { x: 0, y: 0 };

        this.avatar.setLocation({
            x: this.avatar.location.x + velocity.x,
            y: this.avatar.location.y + velocity.y,
        } as Point);

        // Update the direction based on movement
        // if (velocity.x < 0 && this.avatar.direction !== Direction.Left) {
        //     this.avatar.setDirection(Direction.Left);
        // } else if (
        //     velocity.x > 0 &&
        //     this.avatar.direction !== Direction.Right
        // ) {
        //     this.avatar.setDirection(Direction.Right);
        // }

        return velocity;
    };

    moveTo(location: Point, updateDirection: boolean = true): Vector2 {
        if (!this.avatar) return { x: 0, y: 0 };

        const pos = this.avatar.location;
        this.avatar.setLocation(location);

        if (!updateDirection) return { x: 0, y: 0 };

        return {
            x: location.x - pos.x,
            y: location.y - pos.y,
        };
    }

    // Slide to a target location with a specified speed.
    slideTo = (
        location: Point,
        onComplete?: () => void,
        onSlide?: (v: Vector2) => void,
    ) => {
        this._targetLocation = location;
        if (onComplete) this._onSlideComplete = onComplete;
        else this._onSlideComplete = () => {};

        if (onSlide) this._onSlide = onSlide;
        else this._onSlide = () => {};
        this._sliding = true;
    };

    _updateSlide = (delta: number) => {
        if (!this.avatar) return;
        if (!this._targetLocation) return;

        // Get the current position of the avatar and the target location.
        const pos = this.avatar.location;
        const target = this._targetLocation;

        // Get the speed based on the avatar's state.
        const slideSpeed =
            this.avatar.state === "run" ? this.runSpeed : this.walkSpeed;

        // Calculate the distance to the target.
        const dx = target.x - pos.x;
        const dy = target.y - pos.y;
        const distance = Math.hypot(dx, dy);

        // This is the distance the avatar should move towards the target in this frame.
        const deltaSpeed = slideSpeed * delta;

        // If the distance to the target is less than the delta speed,
        // we can snap the avatar to the target location and stop sliding.
        if (distance < deltaSpeed) {
            // Snap to target and stop
            this.avatar.setLocation(target);
            this._sliding = false;
            if (this._onSlideComplete) this._onSlideComplete();
            return;
        }

        // Normalized direction vector * deltaSpeed = displacement this frame
        const direction = {
            x: (dx / distance) * deltaSpeed,
            y: (dy / distance) * deltaSpeed,
        };

        // Move the avatar towards the target location.
        this._onSlide(this.move(direction));
    };

    // Animator wrappers
    playAnimation = (animation: string) => this.avatar.animator.play(animation);
    get loopAnimation() {
        return this.avatar.animator.loop;
    }
    set loopAnimation(value: boolean) {
        this.avatar.animator.loop = value;
    }
}
